"use client";

import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";

const caveat = Caveat({
  subsets: ["latin"],
  weight: ["700"],
  variable: "--font-caveat",
});

/* ─────────────────────────────────────────────────────────────
   1. CONSTRUCTION BUILDER: GROUNDBREAKING PROJECT MANAGEMENT SECTION
   Exact reproduction of Odoo's Construction Project & Task Hub UI
   ───────────────────────────────────────────────────────────── */
export function ConstructionGroundbreakingProjectManagementSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-center">

          {/* LEFT COLUMN: TITLE, COPYWRITING & TESTIMONIAL BUBBLE */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 space-y-4 text-left relative"
          >
            {/* Title with Pink Wavy Underline under "project" */}
            <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1] relative`}>
              <div>
                <span>Groundbreaking </span>
                <span className="relative inline-block px-1">
                  project
                  <svg
                    className="absolute -bottom-1 left-0 w-full h-3 text-[#FF6B6B] overflow-visible"
                    viewBox="0 0 100 12"
                    fill="none"
                  >
                    <path
                      d="M 2 6 Q 25 1, 50 6 T 98 6"
                      stroke="#FF6B6B"
                      strokeWidth="5"
                      strokeLinecap="round"
                      fill="none"
                    />
                  </svg>
                </span>
              </div>
              <div>management</div>
            </h2>

            {/* Subtext Copywriting */}
            <div className="space-y-3">
              <p className="text-xl leading-relaxed text-gray-300">
                <strong className="text-white font-bold">All-in-one project hub.</strong> Manage all aspects of your construction project from one centralized dashboard. Use the top bar to handle all related records, from stock moves to planning shifts.
              </p>
            </div>

            {/* Bottom Testimonial Pill Badge */}
            <div className="pt-3 flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full overflow-hidden border border-slate-300 shadow-md">
                <img src="/odoo/unsplash/avatars/u3.jpg" alt="Bob Bloom" className="w-full h-full object-cover" />
              </div>
               <div className="bg-white/10 text-slate-800 rounded-full shadow-2xl p-2.5 sm:p-3 px-5 sm:px-8 border border-slate-100 text-center">
              <i className="text-white text-xs sm:text-base font-semibold block leading-snug">
                The best tool I ever had!
              </i>
            </div>
            </div>
          </motion.div>

          {/* RIGHT COLUMN: ODOO CONSTRUCTION KANBAN MOCKUP CARD (Small & Precise max-w-[540px]) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-6 relative w-full flex justify-center lg:justify-end"
          >
            {/* White Window Container */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-[10px] sm:text-[11.5px] w-full p-3 sm:p-4 font-sans">
              
              {/* Odoo Top Global Header Navigation */}
              <div className="border-b border-slate-100 pb-2 mb-2 space-y-1.5">
                <div className="flex flex-wrap items-center justify-between text-[8px] text-slate-500 font-medium">
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-600 font-bold flex items-center gap-0.5">✔️ Project</span>
                    <span className="cursor-pointer hover:text-slate-900">Projects</span>
                    <span className="cursor-pointer hover:text-slate-900">Tasks</span>
                    <span className="cursor-pointer hover:text-slate-900">Reporting</span>
                    <span className="cursor-pointer hover:text-slate-900">Configuration</span>
                  </div>
                  <div className="flex items-center gap-1 text-[7.5px]">
                    <span>💬 3</span>
                    <span>🔔 11</span>
                    <span className="font-bold text-slate-800">🛠️ BuildScape</span>
                  </div>
                </div>

                {/* Sub Nav Bar Tabs */}
                <div className="flex flex-wrap items-center gap-1 pt-1 text-[8px] font-medium text-slate-600">
                  <span className="bg-emerald-50 text-emerald-700 font-bold border border-emerald-200 px-2 py-0.5 rounded cursor-pointer">Tasks</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">Timesheets</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">Planning</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">Documents</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">Invoices</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">Purchase Orders</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">From WH</span>
                  <span className="cursor-pointer hover:text-slate-900 px-1">Dashboard</span>
                </div>

                {/* Sub Header / Breadcrumb & Search */}
                <div className="flex flex-wrap items-center justify-between gap-1 pt-1 text-[8px]">
                  <div className="flex items-center gap-1">
                    <span className="bg-[#7C5A76] text-white px-1.5 py-0.2 rounded font-semibold text-[7.5px]">New</span>
                    <span className="text-slate-500 font-medium truncate max-w-[180px]">Projects / <strong className="text-slate-800">Bloom office renovations ⚙️</strong></span>
                  </div>
                  <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-[7.5px] text-slate-400">
                    <span>🔍 Search...</span>
                  </div>
                </div>
              </div>

              {/* Tasks Kanban Columns (4 columns) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[8px]">
                
                {/* Column 1: New (3) */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[8.5px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                    <span>New</span>
                    <div className="flex items-center gap-0.5">
                      <span className="text-slate-400 font-normal">+</span>
                      <span className="text-slate-500 font-semibold text-[7.5px]">3</span>
                    </div>
                  </div>
                  <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="w-2/3 bg-rose-500 h-full" />
                    <div className="w-1/3 bg-amber-400 h-full" />
                  </div>

                  {/* Card 1 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8px] block font-bold">Renovation pack</strong>
                    <span className="text-slate-400 text-[7px] block">Bob Bloom</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-rose-50 text-rose-700 border border-rose-200 text-[6.5px] px-1 rounded font-semibold">Renovation</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7px]">
                      <span className="text-slate-400">🕒 400:00</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    </div>
                  </div>

                  {/* Card 2 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8px] block font-bold">Customer meeting</strong>
                    <span className="text-slate-400 text-[7px] block">Bob Bloom</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-purple-50 text-purple-700 border border-purple-200 text-[6.5px] px-1 rounded font-semibold">Admin</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7px]">
                      <span className="text-slate-400">📞 ✉️</span>
                      <span className="text-amber-500 font-bold">⚠️</span>
                    </div>
                  </div>

                  {/* Card 3 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8px] block font-bold">Foundations</strong>
                    <span className="text-slate-400 text-[7px] block">Bob Bloom</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-rose-50 text-rose-700 border border-rose-200 text-[6.5px] px-1 rounded font-semibold">Renovation</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7px]">
                      <span className="text-rose-500 font-bold">☆ ✔️</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    </div>
                  </div>
                </div>

                {/* Column 2: Ongoing (3) */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[8.5px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                    <span>Ongoing</span>
                    <div className="flex items-center gap-0.5">
                      <span className="text-slate-400 font-normal">+</span>
                      <span className="text-slate-500 font-semibold text-[7.5px]">3</span>
                    </div>
                  </div>
                  <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="w-2/3 bg-emerald-500 h-full" />
                    <div className="w-1/3 bg-amber-400 h-full" />
                  </div>

                  {/* Card 1 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8px] block font-bold">Floor plan</strong>
                    <span className="text-slate-400 text-[7px] block">Bob Bloom</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-purple-50 text-purple-700 border border-purple-200 text-[6.5px] px-1 rounded font-semibold">Admin</span>
                      <span className="bg-rose-50 text-rose-700 border border-rose-200 text-[6.5px] px-1 rounded font-semibold">Renovation</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7px]">
                      <span className="text-amber-500 font-bold">Tomorrow ⭐ ✉️</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    </div>
                  </div>

                  {/* Card 2 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8px] block font-bold">Architect consultation</strong>
                    <span className="text-slate-400 text-[7px] block">Bob Bloom</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-purple-50 text-purple-700 border border-purple-200 text-[6.5px] px-1 rounded font-semibold">Admin</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7px]">
                      <span className="text-amber-400">⭐ <span className="text-slate-400">🕒 02:00</span></span>
                      <span className="text-amber-500 font-bold">⚠️</span>
                    </div>
                  </div>

                  {/* Card 3 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8px] block font-bold">Masonry plan</strong>
                    <span className="text-slate-400 text-[7px] block">Bob Bloom</span>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7px]">
                      <span className="text-slate-300">☆</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    </div>
                  </div>
                </div>

                {/* Column 3: Onsite work (2) */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[8.5px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                    <span>Onsite work</span>
                    <div className="flex items-center gap-0.5">
                      <span className="text-slate-400 font-normal">+</span>
                      <span className="text-slate-500 font-semibold text-[7.5px]">2</span>
                    </div>
                  </div>
                  <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="w-full bg-emerald-500 h-full" />
                  </div>

                  {/* Card 1 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8px] block font-bold">Site prep</strong>
                    <span className="text-slate-400 text-[7px] block">Bob Bloom</span>
                    <span className="text-amber-600 font-bold text-[6.5px] block">Today</span>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7px]">
                      <span className="text-amber-400">⭐ <span className="text-slate-400">🕒 02:00</span></span>
                      <span className="text-amber-500 font-bold">⚠️</span>
                    </div>
                  </div>

                  {/* Card 2 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8px] block font-bold">Demolition</strong>
                    <span className="text-slate-400 text-[7px] block">Bob Bloom</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-teal-50 text-teal-700 border border-teal-200 text-[6.5px] px-1 rounded font-semibold">Construction</span>
                    </div>
                    <span className="text-slate-400 text-[6.5px] block">In 7 days</span>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7px]">
                      <span className="text-slate-300">☆ <span className="text-slate-400">🕒 14:00</span></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    </div>
                  </div>
                </div>

                {/* Column 4: Finishing touches */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[8.5px] font-bold text-slate-800 border-b border-slate-100 pb-1">
                    <span>Finishing touches</span>
                    <div className="flex items-center gap-0.5">
                      <span className="text-slate-400 font-normal">+</span>
                    </div>
                  </div>
                  <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="w-full bg-teal-400 h-full" />
                  </div>

                  {/* Card 1 */}
                  <div className="bg-white border border-slate-200 rounded p-1.5 shadow-sm space-y-1">
                    <strong className="text-slate-900 text-[8px] block font-bold">Site visit</strong>
                    <span className="text-slate-400 text-[7px] block">Bob Bloom</span>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="bg-teal-50 text-teal-700 border border-teal-200 text-[6.5px] px-1 rounded font-semibold">Construction</span>
                    </div>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[7px]">
                      <span className="text-rose-500 font-bold">⭐ ✔️</span>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   2. CONSTRUCTION BUILDER: NEVER LOSE TRACK OF MATERIALS SECTION
   Exact reproduction of Odoo's Materials & Purchase Orders Table UI
   ───────────────────────────────────────────────────────────── */
export function ConstructionNeverLoseTrackOfMaterialsSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto">

        {/* HEADER: HANDWRITTEN TITLE WITH OPEN BOX ICON & UNDERLINE */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-12 text-left max-w-4xl space-y-3"
        >
          {/* Top Yellow Open Box Icon */}
          <div className="inline-block relative">
            <svg className="w-12 h-12 text-amber-400" viewBox="0 0 50 50" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M 10 20 L 25 10 L 40 20 L 25 30 Z" fill="#F59E0B" fillOpacity="0.2" />
              <path d="M 10 20 V 35 L 25 45 V 30 Z" fill="#F59E0B" fillOpacity="0.4" />
              <path d="M 40 20 V 35 L 25 45 V 30 Z" fill="#F59E0B" fillOpacity="0.3" />
              <path d="M 10 20 L 5 12 L 20 5 L 25 10 Z" fill="#FBBF24" fillOpacity="0.5" />
              <path d="M 40 20 L 45 12 L 30 5 L 25 10 Z" fill="#FBBF24" fillOpacity="0.5" />
            </svg>
          </div>

          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            <span>Never lose </span>
            <span className="relative inline-block px-1">
              track
              <span className="absolute bottom-1 left-0 w-full h-3 bg-amber-400 -z-10 rounded-full opacity-90" />
            </span>
            <span> of </span>
            <span className="relative inline-block px-1">
              materials
              <span className="absolute bottom-1 left-0 w-full h-3 bg-amber-400 -z-10 rounded-full opacity-90" />
            </span>
          </h2>

          {/* 3 Subtext Paragraphs Grid (2 Columns) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 pt-2">
            <p className="text-xl leading-relaxed text-gray-300">
              <strong className="text-white font-bold">Streamline purchasing.</strong> Keep track of all the vendor orders and optimize procurement with automated purchase agreements.
            </p>
            <p className="text-xl leading-relaxed text-gray-300">
              <strong className="text-white font-bold">Barcode scanning.</strong> Use any type of barcode scanner to simplify inventory operations.
            </p>
          </div>
        </motion.div>

        {/* MAIN SECTION GRID (LEFT COPYWRITING + RIGHT SMALL PURCHASE TABLE) */}
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-center">

          {/* LEFT COLUMN: MATERIAL CONTROL COPYWRITING */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 space-y-4 text-left"
          >
            <p className="text-gray-300 text-xl leading-relaxed">
              <strong className="text-white font-bold">Material control.</strong> Track all materials you store and use on construction jobs. Inventory features include variants, UoM, packaging, serial numbers, and lots.
            </p>
          </motion.div>

          {/* RIGHT COLUMN: PURCHASE ORDERS TABLE CARD (Small & Precise max-w-[540px]) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 relative w-full flex justify-center lg:justify-end"
          >
            {/* White Table Window Container (Small and precise max-w-[540px]) */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-[8.5px] sm:text-[9.5px] w-full max-w-[540px] p-2.5 sm:p-3.5 font-sans">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[8px] sm:text-[8.5px] border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 text-slate-400 font-semibold text-[7.5px]">
                      <th className="pb-1.5 px-1 w-5"><input type="checkbox" className="rounded" /></th>
                      <th className="pb-1.5 px-1">Reference</th>
                      <th className="pb-1.5 px-1">Confirmation Date</th>
                      <th className="pb-1.5 px-1">Vendor</th>
                      <th className="pb-1.5 px-1">Buyer</th>
                      <th className="pb-1.5 px-1 text-right">Total</th>
                      <th className="pb-1.5 px-1">Status</th>
                      <th className="pb-1.5 px-1 text-right">Expected Arrival</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 text-slate-700">
                    {/* Row 1 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1 px-1"><input type="checkbox" className="rounded" /></td>
                      <td className="py-1 px-1 font-bold text-slate-800 flex items-center gap-0.5">⭐ <span className="text-sky-600">P00019</span></td>
                      <td className="py-1 px-1 text-slate-500 text-[7.5px]">09/03/2024 10:45:28</td>
                      <td className="py-1 px-1 text-slate-700 font-medium">Bro's Bricks</td>
                      <td className="py-1 px-1 text-slate-600">Leo Moore</td>
                      <td className="py-1 px-1 text-right font-semibold">$ 4,567.80</td>
                      <td className="py-1 px-1"><span className="text-slate-600 font-medium">Purchase Order</span></td>
                      <td className="py-1 px-1 text-right text-slate-500 text-[7.5px]">09/25/2024</td>
                    </tr>

                    {/* Row 2 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1 px-1"><input type="checkbox" className="rounded" /></td>
                      <td className="py-1 px-1 font-bold text-slate-800 flex items-center gap-0.5">⭐ <span className="text-sky-600">P00018</span></td>
                      <td className="py-1 px-1 text-slate-500 text-[7.5px]">09/03/2024 10:44:40</td>
                      <td className="py-1 px-1 text-slate-700 font-medium">Rob Robinson</td>
                      <td className="py-1 px-1 text-slate-600">Leo Moore</td>
                      <td className="py-1 px-1 text-right font-semibold">$ 138.00</td>
                      <td className="py-1 px-1"><span className="text-slate-600 font-medium">Purchase Order</span></td>
                      <td className="py-1 px-1 text-right text-slate-500 text-[7.5px]">09/06/2024</td>
                    </tr>

                    {/* Row 3 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1 px-1"><input type="checkbox" className="rounded" /></td>
                      <td className="py-1 px-1 font-bold text-slate-800 flex items-center gap-0.5">⭐ <span className="text-sky-600">P00017</span></td>
                      <td className="py-1 px-1 text-slate-500 text-[7.5px]">09/03/2024 10:42:34</td>
                      <td className="py-1 px-1 text-slate-700 font-medium">Renov materials</td>
                      <td className="py-1 px-1 text-slate-600">Leo Moore</td>
                      <td className="py-1 px-1 text-right font-semibold">$ 1,414.50</td>
                      <td className="py-1 px-1"><span className="text-slate-600 font-medium">Purchase Order</span></td>
                      <td className="py-1 px-1 text-right text-slate-500 text-[7.5px]">09/03/2024</td>
                    </tr>

                    {/* Row 4 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1 px-1"><input type="checkbox" className="rounded" /></td>
                      <td className="py-1 px-1 font-bold text-slate-800 flex items-center gap-0.5">⭐ <span className="text-sky-600">P00015</span></td>
                      <td className="py-1 px-1 text-slate-500 text-[7.5px]">09/03/2024 10:42:13</td>
                      <td className="py-1 px-1 text-slate-700 font-medium">Maria's Doors</td>
                      <td className="py-1 px-1 text-slate-600">Leo Moore</td>
                      <td className="py-1 px-1 text-right font-semibold">$ 115.00</td>
                      <td className="py-1 px-1"><span className="text-slate-600 font-medium">Purchase Order</span></td>
                      <td className="py-1 px-1 text-right text-slate-500 text-[7.5px]">09/03/2024</td>
                    </tr>

                    {/* Row 5 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1 px-1"><input type="checkbox" className="rounded" /></td>
                      <td className="py-1 px-1 font-bold text-slate-800 flex items-center gap-0.5">⭐ <span className="text-sky-600">P00011</span></td>
                      <td className="py-1 px-1 text-slate-500 text-[7.5px]">09/03/2024 10:40:47</td>
                      <td className="py-1 px-1 text-slate-700 font-medium">Casa Muros</td>
                      <td className="py-1 px-1 text-slate-600">Leo Moore</td>
                      <td className="py-1 px-1 text-right font-semibold">$ 115.00</td>
                      <td className="py-1 px-1"><span className="text-slate-600 font-medium">Purchase Order</span></td>
                      <td className="py-1 px-1 text-right text-slate-500 text-[7.5px]">09/18/2024</td>
                    </tr>

                    {/* Row 6 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1 px-1"><input type="checkbox" className="rounded" /></td>
                      <td className="py-1 px-1 font-bold text-slate-800 flex items-center gap-0.5">☆ <span className="text-sky-600">P00016</span></td>
                      <td className="py-1 px-1 text-slate-500 text-[7.5px]">09/03/2024 10:42:31</td>
                      <td className="py-1 px-1 text-slate-700 font-medium">Cement Kings</td>
                      <td className="py-1 px-1 text-slate-600">Leo Moore</td>
                      <td className="py-1 px-1 text-right font-semibold">$ 759.00</td>
                      <td className="py-1 px-1"><span className="text-slate-600 font-medium">Purchase Order</span></td>
                      <td className="py-1 px-1 text-right text-slate-500 text-[7.5px]">09/18/2024</td>
                    </tr>

                    {/* Row 7 */}
                    <tr className="hover:bg-slate-50">
                      <td className="py-1 px-1"><input type="checkbox" className="rounded" /></td>
                      <td className="py-1 px-1 font-bold text-slate-800 flex items-center gap-0.5">☆ <span className="text-sky-600">P00013</span></td>
                      <td className="py-1 px-1 text-slate-500 text-[7.5px]">09/03/2024 10:41:35</td>
                      <td className="py-1 px-1 text-slate-700 font-medium">Cement Kings</td>
                      <td className="py-1 px-1 text-slate-600">Leo Moore</td>
                      <td className="py-1 px-1 text-right font-semibold">$ 165.60</td>
                      <td className="py-1 px-1"><span className="text-slate-600 font-medium">Purchase Order</span></td>
                      <td className="py-1 px-1 text-right text-slate-500 text-[7.5px]">09/03/2024</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>

        </div>

        {/* BOTTOM CENTERED DEVICE TESTIMONIAL & INVENTORY HARDWARE BUTTON */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 sm:mt-14 flex flex-col items-center justify-center text-center space-y-3"
        >
          {/* Bob Bloom Testimonial Bubble */}
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full overflow-hidden border border-slate-300 shadow-md">
              <img src="/odoo/unsplash/avatars/u3.jpg" alt="Bob Bloom" className="w-full h-full object-cover" />
            </div>
             <div className="bg-white/10 text-slate-800 rounded-full shadow-2xl p-2.5 sm:p-3 px-5 sm:px-8 border border-slate-100 text-center">
              <i className="text-white text-xs sm:text-base font-semibold block leading-snug">
                Stop chasing, start earning.
              </i>
            </div>
          </div>

          {/* Downward Arrow */}
          <div className="text-slate-400 text-lg">
            ↓
          </div>

          {/* Light Purple Inventory Hardware Button */}
          <div>
            <button className="bg-[#F3E8F4] text-[#7C5A76] font-semibold text-xs sm:text-sm px-5 py-2.5 rounded-lg shadow-sm hover:bg-[#EBDBF5] transition-colors border border-purple-100/50">
              Inventory Hardware
            </button>
          </div>
        </motion.div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   3. CONSTRUCTION BUILDER: KEEP AN EYE ON THE COSTS SECTION
   Exact reproduction of Odoo's Proforma Invoice & Cost Tracking UI
   ───────────────────────────────────────────────────────────── */
export function ConstructionKeepAnEyeOnTheCostsSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">

          {/* LEFT COLUMN: PROFORMA INVOICE CARD WITH TOP-LEFT SPARKLES (Small & Precise max-w-[420px]) */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 relative w-full flex justify-center lg:justify-start"
          >
            <div className="relative w-full max-w-[420px]">

              {/* Top Left Blue Sparkle Accents 💫 */}
              <div className="absolute -top-8 -left-6 z-20 text-sky-400">
                <svg className="w-12 h-12" viewBox="0 0 50 50" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="25" y1="10" x2="25" y2="2" />
                  <line x1="12" y1="15" x2="4" y2="8" />
                  <line x1="38" y1="15" x2="46" y2="8" />
                  <line x1="8" y1="28" x2="1" y2="34" />
                </svg>
              </div>

              {/* White Proforma Invoice Paper Window */}
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 text-[8.5px] sm:text-[9.5px] p-5 sm:p-7 font-sans space-y-4">
                
                {/* Invoice Header: Company Logo & Customer */}
                <div className="flex items-start justify-between border-b border-slate-100 pb-3">
                  <div className="space-y-1">
                    <div className="w-7 h-7 bg-slate-900 rounded flex items-center justify-center text-amber-400 font-bold text-[10px]">
                      ⚒️
                    </div>
                    <strong className="text-slate-900 font-bold text-[9px] block">BuildScape</strong>
                    <span className="text-slate-400 text-[7.5px] block">Belgium</span>
                  </div>
                  <div className="text-right">
                    <span className="text-slate-700 font-semibold text-[8.5px]">Bob Bloom</span>
                  </div>
                </div>

                {/* Proforma Title & Invoice Info */}
                <div className="space-y-2">
                  <h3 className="text-amber-800 font-bold text-xs sm:text-sm tracking-wide">
                    PROFORMA Invoice INV/2024/00009
                  </h3>
                  
                  <div className="grid grid-cols-3 gap-2 text-[7.5px] text-slate-500 border-b border-slate-100 pb-2">
                    <div>
                      <span className="block font-semibold text-slate-700">Invoice Date</span>
                      <span>09/09/2024</span>
                    </div>
                    <div>
                      <span className="block font-semibold text-slate-700">Due Date</span>
                      <span>09/09/2024</span>
                    </div>
                    <div>
                      <span className="block font-semibold text-slate-700">Source</span>
                      <span>S00057</span>
                    </div>
                  </div>
                </div>

                {/* Line Items Table */}
                <table className="w-full text-left border-collapse text-[8px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-400 text-[7px] font-semibold">
                      <th className="pb-1">Description</th>
                      <th className="pb-1 text-center">Quantity</th>
                      <th className="pb-1 text-right">Unit Price</th>
                      <th className="pb-1 text-right">Taxes</th>
                      <th className="pb-1 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 text-slate-700">
                    <tr>
                      <td className="py-1">Floor panels</td>
                      <td className="py-1 text-center">10.00 Units</td>
                      <td className="py-1 text-right">65.00</td>
                      <td className="py-1 text-right">15%</td>
                      <td className="py-1 text-right font-semibold">$ 650.00</td>
                    </tr>
                    <tr>
                      <td className="py-1">Labor hour</td>
                      <td className="py-1 text-center">15.00 Hours</td>
                      <td className="py-1 text-right">60.00</td>
                      <td className="py-1 text-right">—</td>
                      <td className="py-1 text-right font-semibold">$ 900.00</td>
                    </tr>
                    <tr>
                      <td className="py-1">Cement</td>
                      <td className="py-1 text-center">10.00 Units</td>
                      <td className="py-1 text-right">65.00</td>
                      <td className="py-1 text-right">15%</td>
                      <td className="py-1 text-right font-semibold">$ 650.00</td>
                    </tr>
                    <tr>
                      <td className="py-1">Concrete</td>
                      <td className="py-1 text-center">5.00 kg</td>
                      <td className="py-1 text-right">20.00</td>
                      <td className="py-1 text-right">15%</td>
                      <td className="py-1 text-right font-semibold">$ 100.00</td>
                    </tr>
                  </tbody>
                </table>

                {/* Totals Summary */}
                <div className="border-t border-slate-200 pt-2 space-y-1 text-right text-[8px]">
                  <div className="flex justify-between text-slate-500 text-[7.5px]">
                    <span>Untaxed Amount</span>
                    <span className="font-semibold text-slate-800">$ 2,300.00</span>
                  </div>
                  <div className="flex justify-between text-slate-500 text-[7.5px]">
                    <span>Tax 15% on $ 1,400.00</span>
                    <span className="font-semibold text-slate-800">$ 210.00</span>
                  </div>
                  <div className="flex justify-between text-amber-800 font-bold text-[9.5px] pt-1 border-t border-slate-100">
                    <span>Total</span>
                    <span>$ 2,510.00</span>
                  </div>
                </div>

                {/* Invoice Footer */}
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-[7px] text-slate-400">
                  <span>Payment Communication: <strong>INV/2024/00009</strong></span>
                  <span>ccfg@example.com</span>
                  <span>Page 1/1</span>
                </div>

              </div>
            </div>
          </motion.div>

          {/* RIGHT COLUMN: HANDWRITTEN TITLE, BLUE EYE ICON & COPYWRITING */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-6 space-y-5 text-left"
          >
            {/* Blue Eye Icon 👁️ */}
            <div className="inline-block relative">
              <svg className="w-14 h-14 text-sky-400" viewBox="0 0 50 50" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M 5 25 C 12 12, 38 12, 45 25 C 38 38, 12 38, 5 25 Z" fill="#38BDF8" fillOpacity="0.2" />
                <circle cx="25" cy="25" r="7" fill="#0284C7" />
                <circle cx="27" cy="23" r="2.5" fill="#FFFFFF" />
                {/* Eyelashes */}
                <line x1="12" y1="12" x2="8" y2="7" />
                <line x1="25" y1="8" x2="25" y2="2" />
                <line x1="38" y1="12" x2="42" y2="7" />
                <line x1="12" y1="38" x2="8" y2="43" />
                <line x1="25" y1="42" x2="25" y2="48" />
                <line x1="38" y1="38" x2="42" y2="43" />
              </svg>
            </div>

            {/* Title with Blue Highlighter Pill under "costs" */}
            <h2 className={`${caveat.className} text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
              <span>Keep an eye on the </span>
              <span className="relative inline-block px-3 py-0.5 bg-[#00A0DC] text-white rounded-2xl transform -rotate-1 shadow-md">
                costs
              </span>
            </h2>

            {/* 3 Copywriting Paragraph Blocks */}
            <div className="space-y-4">
              <p className="text-xl leading-relaxed text-gray-300">
                <strong className="text-white font-bold">Invoice on YOUR terms.</strong> Charge customers upfront, after the job, or using down payments. Easily invoice by time and materials.
              </p>
              <p className="text-xl leading-relaxed text-gray-300">
                <strong className="text-white font-bold">Make every hour count.</strong> Log timesheets by hours or full days, using a desktop or a mobile device.
              </p>
              <p className="text-xl leading-relaxed text-gray-300">
                <strong className="text-white font-bold">Master the analytic overview.</strong> Easily keep track of expenses, and analyze cost distribution to ensure you turn profit on each job.
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   4. CONSTRUCTION BUILDER: ON TO SOME FINAL TOUCHES SECTION
   Field Service Planner + Geo Map UI
   ───────────────────────────────────────────────────────────── */
export function ConstructionFinalTouchesSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto space-y-8">

        {/* HEADER: HANDWRITTEN TITLE WITH TEAL EXCLAMATION */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="space-y-3 max-w-2xl"
        >
          <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
            On to some final touches
            <span className="text-teal-400">!</span>
          </h2>
          <p className="text-xl leading-relaxed text-gray-300">
            <strong className="text-white font-bold">After-sales support.</strong> Clients will forget the issue but remember the service. Use Helpdesk and Field Service apps to provide five-star support at all times.
          </p>
        </motion.div>

        {/* TWO MOCKUP CARDS SIDE BY SIDE (Small & Precise) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5 items-start">

          {/* LEFT CARD: FIELD SERVICE PLANNER (max-w-[580px]) */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 w-full"
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 text-[8px] sm:text-[8.5px] p-2.5 sm:p-3.5 font-sans w-full max-w-[580px]">

              {/* Top Nav Bar */}
              <div className="border-b border-slate-100 pb-2 mb-2 space-y-1.5">
                <div className="flex flex-wrap items-center justify-between text-[7.5px] text-slate-500 font-medium gap-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-rose-500 font-bold">🚀 Field Service</span>
                    <span>My Tasks</span>
                    <span>All Tasks</span>
                    <span>Planning</span>
                    <span>Reporting</span>
                    <span>Configuration</span>
                  </div>
                  <div className="flex items-center gap-1 text-[7px]">
                    <span>💬</span><span>🔔</span><span>👤</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-[7.5px]">
                  <span className="bg-[#7C5A76] text-white px-1.5 py-0.5 rounded font-semibold text-[7px]">New</span>
                  <span className="font-semibold text-slate-700">My Tasks ⚙️</span>
                  <div className="ml-auto flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-[7px] text-slate-400">
                    <span>🔍</span>
                    <span className="bg-slate-200 text-slate-600 px-1 rounded text-[6.5px]">Open Tasks ×</span>
                    <span>Search...</span>
                  </div>
                </div>
              </div>

              {/* 3 Day Columns */}
              <div className="grid grid-cols-3 gap-1.5 text-[7.5px]">

                {/* Column 1: 05 Jan (9 tasks) */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between font-bold text-slate-800 border-b border-slate-100 pb-0.5">
                    <span>05 Jan</span>
                    <span className="text-slate-400 text-[7px]">9</span>
                  </div>
                  <div className="h-0.5 w-full flex">
                    <div className="w-2/3 bg-rose-500 h-full" />
                    <div className="w-1/3 bg-amber-400 h-full" />
                  </div>

                  {/* Task 1 */}
                  <div className="bg-white border border-slate-200 rounded p-1 shadow-sm space-y-0.5">
                    <strong className="text-slate-900 text-[7.5px] font-bold block">Boiler maintenance</strong>
                    <span className="text-slate-400 text-[6.5px] block">Device Installation and Maintenance</span>
                    <span className="text-slate-400 text-[6.5px] block">Field Service</span>
                    <span className="text-slate-500 text-[6.5px] block">Azure Interior, Colleen Diaz • Fremont</span>
                    <span className="bg-rose-500 text-white text-[6px] px-1 py-0.2 rounded font-semibold">Heating</span>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[6.5px]">
                      <span className="text-slate-500">08:00</span>
                      <span className="text-slate-400">⭐ 🕒 4:00</span>
                      <span className="flex gap-0.5"><span className="w-2 h-2 rounded-full bg-amber-500 block" /><span className="w-2 h-2 rounded-full bg-emerald-400 block" /></span>
                    </div>
                  </div>

                  {/* Task 2 */}
                  <div className="bg-white border border-slate-200 rounded p-1 shadow-sm space-y-0.5">
                    <strong className="text-slate-900 text-[7.5px] font-bold block">Light switch replacement</strong>
                    <span className="text-slate-400 text-[6.5px] block">Device Installation and Maintenance</span>
                    <span className="text-slate-400 text-[6.5px] block">Field Service</span>
                    <span className="text-slate-500 text-[6.5px] block">Wood Corner, Willie Burke • Turlock</span>
                    <span className="bg-amber-500 text-white text-[6px] px-1 py-0.2 rounded font-semibold">Electric</span>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[6.5px]">
                      <span className="text-slate-500">10:00</span>
                      <span className="text-slate-300">☆ 🕒</span>
                      <span className="w-2 h-2 rounded-full bg-emerald-400 block" />
                    </div>
                  </div>

                  {/* Task 3 */}
                  <div className="bg-white border border-slate-200 rounded p-1 shadow-sm space-y-0.5">
                    <strong className="text-slate-900 text-[7.5px] font-bold block">Fix sink</strong>
                    <span className="text-slate-400 text-[6.5px] block">Device Installation and Maintenance</span>
                    <span className="text-slate-400 text-[6.5px] block">Field Service</span>
                    <span className="text-slate-500 text-[6.5px] block">Deco Addict, Addison Olson • Pleasant Hill</span>
                    <span className="bg-sky-500 text-white text-[6px] px-1 py-0.2 rounded font-semibold">Plumbing</span>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[6.5px]">
                      <span className="text-slate-500">10:00</span>
                      <span className="text-slate-300">☆ 🕒</span>
                      <span className="flex gap-0.5"><span className="w-2 h-2 rounded-full bg-amber-500 block" /><span className="text-amber-500">⚠️</span></span>
                    </div>
                  </div>
                </div>

                {/* Column 2: 06 Jan (3 tasks) */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between font-bold text-slate-800 border-b border-slate-100 pb-0.5">
                    <span>06 Jan</span>
                    <span className="text-slate-400 text-[7px]">3</span>
                  </div>
                  <div className="h-0.5 w-full flex">
                    <div className="w-full bg-amber-400 h-full" />
                  </div>

                  <div className="bg-white border border-slate-200 rounded p-1 shadow-sm space-y-0.5">
                    <strong className="text-slate-900 text-[7.5px] font-bold block">Secure basement window</strong>
                    <span className="text-slate-400 text-[6.5px] block">Device Installation and Maintenance</span>
                    <span className="text-slate-400 text-[6.5px] block">Field Service</span>
                    <span className="text-slate-500 text-[6.5px] block">Azure Interior, Colleen Diaz • Fremont</span>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[6.5px]">
                      <span className="text-slate-500">08:00</span>
                      <span className="text-slate-500">01:39</span>
                      <span className="flex gap-0.5"><span className="w-2 h-2 rounded-full bg-amber-500 block" /><span className="w-2 h-2 rounded-full bg-sky-400 block" /></span>
                    </div>
                  </div>

                  <div className="bg-white border border-slate-200 rounded p-1 shadow-sm space-y-0.5">
                    <strong className="text-slate-900 text-[7.5px] font-bold block">Two radiators installation</strong>
                    <span className="text-slate-400 text-[6.5px] block">Device Installation and Maintenance</span>
                    <span className="text-slate-400 text-[6.5px] block">Field Service</span>
                    <span className="text-slate-500 text-[6.5px] block">Wood Corner, Tom Ruiz • Turlock</span>
                    <span className="bg-rose-500 text-white text-[6px] px-1 py-0.2 rounded font-semibold">Heating</span>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[6.5px]">
                      <span className="text-slate-500">09:00</span>
                      <span className="text-slate-300">☆</span>
                      <span className="w-2 h-2 rounded-full bg-emerald-400 block" />
                    </div>
                  </div>

                  <div className="bg-white border border-slate-200 rounded p-1 shadow-sm space-y-0.5">
                    <strong className="text-slate-900 text-[7.5px] font-bold block">Electric installation safety check</strong>
                    <span className="text-slate-400 text-[6.5px] block">Field Service</span>
                    <span className="text-slate-500 text-[6.5px] block">Azure Interior, Nicole Ford • Fremont</span>
                    <span className="bg-amber-500 text-white text-[6px] px-1 py-0.2 rounded font-semibold">Electric</span>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[6.5px]">
                      <span className="text-slate-500">11:00</span>
                      <span className="text-slate-300">☆</span>
                      <span className="w-2 h-2 rounded-full bg-slate-200 block" />
                    </div>
                  </div>
                </div>

                {/* Column 3: 07 Jan (3 tasks) */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between font-bold text-slate-800 border-b border-slate-100 pb-0.5">
                    <span>07 Jan</span>
                    <span className="text-slate-400 text-[7px]">3</span>
                  </div>
                  <div className="h-0.5 w-full flex">
                    <div className="w-1/2 bg-emerald-500 h-full" />
                    <div className="w-1/2 bg-amber-400 h-full" />
                  </div>

                  <div className="bg-white border border-slate-200 rounded p-1 shadow-sm space-y-0.5">
                    <strong className="text-slate-900 text-[7.5px] font-bold block">Find and fix leak</strong>
                    <span className="text-slate-400 text-[6.5px] block">Device Installation and Maintenance</span>
                    <span className="text-slate-400 text-[6.5px] block">Field Service</span>
                    <span className="text-slate-500 text-[6.5px] block">Azure Interior, Brandon Freeman • Fremont</span>
                    <span className="bg-sky-500 text-white text-[6px] px-1 py-0.2 rounded font-semibold">Plumbing</span>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[6.5px]">
                      <span className="text-slate-500">08:00</span>
                      <span className="text-slate-300">☆</span>
                      <span className="text-amber-500">⚠️</span>
                    </div>
                  </div>

                  <div className="bg-white border border-slate-200 rounded p-1 shadow-sm space-y-0.5">
                    <strong className="text-slate-900 text-[7.5px] font-bold block">Install pipeline system</strong>
                    <span className="text-slate-400 text-[6.5px] block">Device Installation and Maintenance</span>
                    <span className="text-slate-400 text-[6.5px] block">Field Service</span>
                    <span className="text-slate-500 text-[6.5px] block">Azure Interior, Nicole Ford • Fremont</span>
                    <span className="bg-sky-500 text-white text-[6px] px-1 py-0.2 rounded font-semibold">Plumbing</span>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[6.5px]">
                      <span className="text-slate-500">08:00</span>
                      <span className="text-slate-300">☆</span>
                      <span className="flex gap-0.5"><span className="w-2 h-2 rounded-full bg-amber-500 block" /><span className="w-2 h-2 rounded-full bg-sky-400 block" /></span>
                    </div>
                  </div>

                  <div className="bg-white border border-slate-200 rounded p-1 shadow-sm space-y-0.5">
                    <strong className="text-slate-900 text-[7.5px] font-bold block">Bathroom ventilation</strong>
                    <span className="text-slate-400 text-[6.5px] block">Field Service</span>
                    <span className="text-slate-500 text-[6.5px] block">Ready Mat, Travis Mendoza • Tracy</span>
                    <div className="flex items-center justify-between pt-0.5 border-t border-slate-50 text-[6.5px]">
                      <span className="text-slate-500">14:00</span>
                      <span className="text-slate-300">☆</span>
                      <span className="w-2 h-2 rounded-full bg-slate-200 block" />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </motion.div>

          {/* RIGHT CARD: GEO MAP MOBILE (max-w-[220px]) */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 w-full flex justify-center lg:justify-start"
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 text-[8px] p-2.5 font-sans w-full max-w-[260px]">
              {/* Mobile Top Bar */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-1.5 mb-1.5 text-[7.5px]">
                <div className="grid grid-cols-9 gap-0.5 text-[6px] text-slate-400 font-medium">
                  <span className="col-span-9">⠿</span>
                </div>
                <div className="flex items-center gap-1 text-[7px]">
                  <span>💬</span><span>🔔</span><span>👤</span>
                </div>
              </div>

              {/* My Tasks Header */}
              <div className="flex items-center justify-between text-[7.5px] font-semibold text-slate-800 mb-1.5">
                <span>My Tasks ⚙️</span>
                <div className="flex items-center gap-1 text-[7px] text-slate-400">
                  <span>1-5 / 5</span>
                  <span>❮ ❯</span>
                  <span>📍</span>
                  <span>🔍</span>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="w-full rounded-xl overflow-hidden border border-slate-200 bg-slate-100 relative" style={{ height: "200px" }}>
                {/* Street grid background */}
                <div className="absolute inset-0 bg-[#E8EEF4]" />
                {/* Water areas */}
                <div className="absolute bottom-0 left-0 w-full h-1/3 bg-[#B8D4E8] opacity-60" />
                {/* Street lines */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200" fill="none">
                  {/* Horizontal streets */}
                  <line x1="0" y1="40" x2="200" y2="40" stroke="#D1D5DB" strokeWidth="2" />
                  <line x1="0" y1="80" x2="200" y2="80" stroke="#D1D5DB" strokeWidth="1.5" />
                  <line x1="0" y1="110" x2="200" y2="110" stroke="#D1D5DB" strokeWidth="1.5" />
                  <line x1="0" y1="135" x2="200" y2="135" stroke="#D1D5DB" strokeWidth="1" />
                  <line x1="0" y1="155" x2="200" y2="155" stroke="#D1D5DB" strokeWidth="1" />
                  {/* Vertical streets */}
                  <line x1="50" y1="0" x2="50" y2="200" stroke="#D1D5DB" strokeWidth="1.5" />
                  <line x1="90" y1="0" x2="90" y2="200" stroke="#D1D5DB" strokeWidth="1.5" />
                  <line x1="130" y1="0" x2="130" y2="200" stroke="#D1D5DB" strokeWidth="2" />
                  <line x1="165" y1="0" x2="165" y2="200" stroke="#D1D5DB" strokeWidth="1" />
                  {/* Diagonal road */}
                  <line x1="50" y1="40" x2="130" y2="135" stroke="#E5E7EB" strokeWidth="2.5" />
                  {/* Route path */}
                  <polyline points="60,80 90,110 110,130 130,145" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" strokeDasharray="4 2" />
                  {/* Map Pins */}
                  <circle cx="60" cy="80" r="8" fill="#EF4444" />
                  <text x="60" y="83" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">1</text>
                  <circle cx="90" cy="60" r="8" fill="#3B82F6" />
                  <text x="90" y="63" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">2</text>
                  <circle cx="110" cy="75" r="8" fill="#10B981" />
                  <text x="110" y="78" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">3</text>
                  <circle cx="130" cy="100" r="8" fill="#F59E0B" />
                  <text x="130" y="103" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">4</text>
                  <circle cx="100" cy="140" r="8" fill="#8B5CF6" />
                  <text x="100" y="143" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">5</text>
                  {/* Zoom buttons */}
                  <rect x="5" y="60" width="14" height="24" rx="3" fill="white" stroke="#E5E7EB" strokeWidth="0.5" />
                  <text x="12" y="73" textAnchor="middle" fill="#374151" fontSize="10" fontWeight="bold">+</text>
                  <text x="12" y="83" textAnchor="middle" fill="#374151" fontSize="10" fontWeight="bold">-</text>
                  {/* Label tags */}
                  <rect x="65" y="72" width="30" height="10" rx="2" fill="white" stroke="#E5E7EB" strokeWidth="0.5" />
                  <text x="80" y="80" textAnchor="middle" fill="#374151" fontSize="5">Paul Robeson St</text>
                  <rect x="50" y="170" width="40" height="10" rx="2" fill="white" stroke="#E5E7EB" strokeWidth="0.5" />
                  <text x="70" y="178" textAnchor="middle" fill="#374151" fontSize="5">Cobblestone District</text>
                </svg>
              </div>
            </div>
          </motion.div>

        </div>

      </div>
    </section>
  );
}
